import { observer } from 'mobx-react-lite'
import type { UIEvent } from 'react'
import { Button } from 'shared/ui'
import dayjs, { DayjsFormats } from 'shared/lib/dayjs'
import { ZIndex } from 'shared/constants/zIndex'
import { useCallPopUpDefaultContext } from 'widgets/CallPopUp/context/callPopUpDefaultContext'
import styles from 'widgets/CallPopUp/ui/styles.module.scss'
import { AccordionSection } from 'widgets/CallPopUp/ui/components/AccordionSection/AccordionSection'
import { ContactsList } from 'widgets/CallPopUp/ui/components/ContactsList/ContactsList'
import { RemoveAction } from 'widgets/CallPopUp/ui/components/ContactsList/parts'

export const CallPowerDialerSection = observer(() => {
  const { callPopUpPowerDialerStoreV2, innerRefTooltipCurrent } = useCallPopUpDefaultContext()
  const {
    remainingContactsCount,
    isPause,
    isSaving,
    handleCheckCredits,
    togglePowerDialerPause,
    handlePowerDialerStop,
    handleSkipContact,
    fetchedContacts,
    currentPowerDialerName,
    handleSaveAndExit,
    fetchNextContacts,
    hasUnfetchedContacts,
    isContactsLoading,
    isNonPowerDialerCallActive,
    isPreCallSetupInProgress,
    isRefreshingPowerDialerContacts,
    onPowerDialerRefresh,
    lastContactsRefreshed,
    isOnlyManualSelection,
  } = callPopUpPowerDialerStoreV2

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement
    const scrollHeight = target.scrollHeight
    const scrollTop = target.scrollTop
    const offsetHeight = target.offsetHeight

    if (isContactsLoading) return
    if (!hasUnfetchedContacts) return
    if (scrollHeight <= scrollTop + offsetHeight + 20) {
      fetchNextContacts()
    }
  }

  const handleRefreshClick = (event: UIEvent) => {
    event.stopPropagation()
    onPowerDialerRefresh()
  }

  return (
    <AccordionSection
      show
      renderButton={
        !isOnlyManualSelection ? (
          <Button
            icon={'refresh'}
            text={'Refresh'}
            typeBtn={'outlined'}
            contained={'secondary'}
            size={'small'}
            disabled={isRefreshingPowerDialerContacts}
            loading={isRefreshingPowerDialerContacts}
            tooltipProps={{
              type: 'description',
              placement: 'top',
              label: 'Last refreshed on',
              desc: lastContactsRefreshed
                ? dayjs(lastContactsRefreshed).format(DayjsFormats.fullWithAtDash2)
                : 'N/A',
              zIndex: ZIndex.OVERLAY_TOP_1,
            }}
            onClick={handleRefreshClick}
          />
        ) : null
      }
      count={remainingContactsCount}
      title={currentPowerDialerName}
      hideChevronOnEmpty
    >
      {remainingContactsCount !== 0 && (
        <>
          <div className={styles.wrapBtn}>
            <Button
              contained={'secondary'}
              iconProps={{
                icon: isPause ? 'play' : 'pause',
              }}
              text={isPause ? 'Resume' : 'Pause'}
              fullWidth
              disabled={isNonPowerDialerCallActive || isPreCallSetupInProgress}
              onClick={() => {
                if (handleCheckCredits()) return

                togglePowerDialerPause()
              }}
            />
            <Button
              contained={'primary'}
              intent={'destructive'}
              iconProps={{
                icon: 'stop',
              }}
              text={'Stop'}
              fullWidth
              disabled={isPreCallSetupInProgress}
              onClick={handlePowerDialerStop}
            />
          </div>

          <ContactsList
            items={fetchedContacts}
            handleScroll={handleScroll}
            loading={isContactsLoading}
            renderActions={(item) => (
              <RemoveAction
                item={item}
                onRemove={handleSkipContact}
                innerRefCurrent={innerRefTooltipCurrent || undefined}
              />
            )}
          />
          {isPause && (
            <Button
              contained={'secondary'}
              text={'Save and exit'}
              loading={isSaving}
              fullWidth
              onClick={handleSaveAndExit}
              className={styles.saveAndExitButton}
            />
          )}
        </>
      )}
    </AccordionSection>
  )
})
